import React, { useState, useRef, useEffect } from 'react';
import { Mic, ArrowUp, Plus, Brain } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

interface SetValueRef {
  get: () => string;
  set: (value: string) => void;
}

// Custom button configuration for extending the input
export interface CustomButton {
  id: string;
  icon: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  activeClassName?: string;
}

interface MobileChatInputProps {
  onSubmit: (message: string) => void | Promise<void>;
  activeAgent?: string;
  isTyping?: boolean;
  onTypingChange?: (isTyping: boolean) => void;
  setValue?: React.MutableRefObject<SetValueRef>;
  isKnowledgeMode?: boolean;
  isListening?: boolean;
  micBlocked?: boolean;
  selectedFiles?: File[];
  isChatMode?: boolean;
  onToggleKnowledge?: () => void;
  onToggleDictation?: () => void;
  onAddAction?: () => void;
  // Extended props for customization
  placeholder?: string;
  defaultValue?: string;
  /** Controlled value - when provided, component becomes controlled */
  value?: string;
  /** Called when value changes (for controlled mode) */
  onValueChange?: (value: string) => void;
  showPlusButton?: boolean;
  showBrainButton?: boolean;
  showMicButton?: boolean;
  customLeftButtons?: CustomButton[];
  customRightButtons?: CustomButton[];
  disabled?: boolean;
  className?: string;
  /** Set to true to disable absolute positioning (useful in flex layouts) */
  inline?: boolean;
  /** Set to false to hide the top shadow (default: true) */
  showShadow?: boolean;
}

// List Menu Icon with dots on right side (thicker strokes)
const ListMenuIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none">
    <line x1="5" y1="6" x2="15" y2="6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="5" y1="12" x2="15" y2="12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="5" y1="18" x2="15" y2="18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="19" cy="6" r="1.5" fill="currentColor" />
    <circle cx="19" cy="12" r="1.5" fill="currentColor" />
    <circle cx="19" cy="18" r="1.5" fill="currentColor" />
  </svg>
);

export const MobileChatInput: React.FC<MobileChatInputProps> = ({
  onSubmit,
  activeAgent,
  isTyping = false,
  onTypingChange,
  setValue,
  isKnowledgeMode,
  isListening: isListeningProp,
  micBlocked = false,
  selectedFiles,
  isChatMode,
  onToggleKnowledge,
  onToggleDictation,
  onAddAction,
  // Extended props
  placeholder = "How do I?",
  defaultValue = "",
  value: controlledValue,
  onValueChange,
  showPlusButton = true,
  showBrainButton = true,
  showMicButton = true,
  customLeftButtons = [],
  customRightButtons = [],
  disabled = false,
  className = "",
  inline = false,
  showShadow = true,
}) => {
  const [internalQuery, setInternalQuery] = useState(defaultValue);

  // Support both controlled and uncontrolled modes
  const isControlled = controlledValue !== undefined;
  const query = isControlled ? controlledValue : internalQuery;

  const setQuery = (newValue: string) => {
    if (isControlled) {
      onValueChange?.(newValue);
    } else {
      setInternalQuery(newValue);
    }
  };

  // Expose get/set methods to parent component via ref
  React.useEffect(() => {
    if (setValue) {
      setValue.current = {
        get: () => query,
        set: (value: string) => setQuery(value)
      };
    }
  }, [setValue, query]);
  const [isListening, setIsListening] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasText = query.trim().length > 0;

  // Auto-resize textarea and notify parent of typing state
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
    onTypingChange?.(hasText);
  }, [query, hasText, onTypingChange]);

  // Auto-focus textarea when chat mode is active
  useEffect(() => {
    if (isChatMode && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isChatMode]);

  const handleSubmit = () => {
    if (!query.trim()) return;
    if (isTyping) return; // Block sends while a response is in flight
    const message = query.trim();
    setQuery(''); // Clear first to prevent double submit
    onSubmit(message);
  };

  const handleSendClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handleSubmit();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleVoiceToggle = () => {
    if (onToggleDictation) {
      onToggleDictation();
    } else {
      setIsListening(!isListening);
      toast.success(isListening ? 'Voice recording stopped' : 'Voice recording started');
    }
  };

  const handlePlusClick = () => {
    if (onAddAction) {
      onAddAction();
    }
  };

  const handleBrainToggle = () => {
    if (onToggleKnowledge) {
      onToggleKnowledge();
    }
  };

  // Render a custom button
  const renderCustomButton = (btn: CustomButton) => (
    <Button
      key={btn.id}
      variant="ghost"
      size="sm"
      onClick={btn.onClick}
      disabled={disabled}
      className={`flex-shrink-0 w-12 h-12 p-0 border-2 rounded-full bg-transparent ${
        btn.active
          ? btn.activeClassName || 'text-foreground border-foreground bg-accent'
          : 'text-muted-foreground hover:text-foreground border-input hover:bg-accent'
      }`}
    >
      {btn.icon}
    </Button>
  );

  return (
    <div
      className={`${inline ? 'relative' : 'absolute left-0 right-0 bottom-0'} ${className}`}
    >
      {/* Input card with wave gradient */}
      <div
        style={{
          borderTopLeftRadius: '30px',
          borderTopRightRadius: '30px',
          background: 'linear-gradient(180deg, hsl(var(--muted)) 0%, hsl(var(--card)) 100%)',
          boxShadow: showShadow ? '0 -9px 18px hsl(var(--foreground) / 0.12)' : 'none',
          borderTop: '1px solid hsl(var(--border))',
          borderLeft: '1px solid hsl(var(--border))',
          borderRight: '1px solid hsl(var(--border))',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
      >
        {/* Text input area */}
        <div className="px-6 pt-5 pb-2">
          <Textarea
            ref={textareaRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoFocus
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            disabled={disabled}
            className="w-full !text-[18x] bg-transparent border-none !font-medium !placeholder:font-medium !text-foreground placeholder:!text-muted-foreground resize-none p-0 !outline-none !ring-0 !border-0 !shadow-none focus-visible:!ring-0 focus-visible:!ring-offset-0 focus-visible:!outline-none"
            style={{
              minHeight: '40px',
              maxHeight: '60px',
              fontSize: '20px',
              fontWeight: 300,
              lineHeight: '20px',
              outline: 'none',
              boxShadow: 'none',
              border: 'none'
            }}
          />
        </div>

        {/* Icon row at the bottom */}
        <div className="flex items-center justify-between px-6 pb-3">
          {/* Left icons */}
          <div className="flex items-center gap-2">
            {showPlusButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePlusClick}
                disabled={disabled}
                className="flex-shrink-0 w-12 h-12 p-0 text-muted-foreground hover:text-foreground border-2 border-input rounded-full bg-transparent hover:bg-accent"
              >
                {/* Sharp edge plus icon */}
                <svg className="h-20 w-20" viewBox="0 0 24 24" fill="none" stroke="currentColor"  strokeWidth="6" strokeLinecap="square">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </Button>
            )}
            {showBrainButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBrainToggle}
                disabled={disabled}
                className={`flex-shrink-0 w-12 h-12 p-0 border-2 rounded-full bg-transparent ${
                  isKnowledgeMode
                    ? 'text-foreground border-foreground bg-accent'
                    : 'text-muted-foreground hover:text-foreground border-input hover:bg-accent'
                }`}
              >
                {isKnowledgeMode ? (
                  <Brain className="h-8 w-8" strokeWidth={2} />
                ) : (
                  <ListMenuIcon className="h-8 w-8" />
                )}
              </Button>
            )}
            {/* Custom left buttons */}
            {customLeftButtons.map(renderCustomButton)}
          </div>

          {/* Right icons */}
          <div className="flex items-center gap-3">
            {/* Custom right buttons */}
            {customRightButtons.map(renderCustomButton)}
            {showMicButton && (
              <Button
                onClick={handleVoiceToggle}
                variant="ghost"
                size="sm"
                disabled={disabled}
                aria-label={
                  isListening || isListeningProp
                    ? 'Stop dictation'
                    : micBlocked
                    ? 'Microphone blocked - tap for help'
                    : 'Start dictation'
                }
                title={micBlocked ? 'Microphone blocked - tap for help on enabling it' : undefined}
                className={`flex-shrink-0 w-12 h-12 p-0 border-2 rounded-full bg-transparent ${
                  isListening || isListeningProp
                    ? 'text-red-500 border-red-400 animate-pulse'
                    : micBlocked
                    ? 'text-amber-600 border-amber-400'
                    : 'text-muted-foreground hover:text-foreground border-input hover:bg-accent'
                }`}
              >
                {/* <Mic className="h-5 w-5" strokeWidth={2.5} /> */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  xmlnsXlink="http://www.w3.org/1999/xlink"
                  className="h-9 w-9"
                  viewBox="0 0 256 256"
                  xmlSpace="preserve"
                >
                  <g
                    style={{
                      stroke: "none",
                      strokeWidth: 1,
                      strokeDasharray: "none",
                      strokeLinecap: "butt",
                      strokeLinejoin: "miter",
                      strokeMiterlimit: 10,
                      fill: "none",
                      fillRule: "nonzero",
                      opacity: 1,
                    }}
                    transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)"
                  >
                    <path
                      d="M 45 70.968 c -16.013 0 -29.042 -13.028 -29.042 -29.042 c 0 -1.712 1.388 -3.099 3.099 -3.099 c 1.712 0 3.099 1.388 3.099 3.099 C 22.157 54.522 32.404 64.77 45 64.77 c 12.595 0 22.843 -10.248 22.843 -22.843 c 0 -1.712 1.387 -3.099 3.099 -3.099 s 3.099 1.388 3.099 3.099 C 74.042 57.94 61.013 70.968 45 70.968 z"
                      style={{
                        stroke: "none",
                        strokeWidth: 2,
                        strokeDasharray: "none",
                        strokeLinecap: "butt",
                        strokeLinejoin: "miter",
                        strokeMiterlimit: 10,
                        fill: "currentColor",
                        fillRule: "nonzero",
                        opacity: 1,
                      }}
                      transform=" matrix(1 0 0 1 0 0) "
                      strokeLinecap="round"
                    />
                    <path
                      d="M 45 55 L 45 55 c -10 0 -18 -8 -18 -18 V 18 C 27 8 35 0 45 0 h 0 c 10 0 18 8 18 18 v 19 C 63 47 55 55 45 55 z"
                      style={{
                        stroke: "none",
                        strokeWidth: 2,
                        strokeDasharray: "none",
                        strokeLinecap: "butt",
                        strokeLinejoin: "miter",
                        strokeMiterlimit: 10,
                        fill: "currentColor",
                        fillRule: "nonzero",
                        opacity: 1,
                      }}
                      transform=" matrix(1 0 0 1 0 0) "
                      strokeLinecap="round"
                    />
                    <path
                      d="M 45 89.213 c -1.712 0 -3.099 -1.387 -3.099 -3.099 V 68.655 c 0 -1.712 1.388 -3.099 3.099 -3.099 c 1.712 0 3.099 1.387 3.099 3.099 v 17.459 C 48.099 87.826 46.712 89.213 45 89.213 z"
                      style={{
                        stroke: "none",
                        strokeWidth: 2,
                        strokeDasharray: "none",
                        strokeLinecap: "butt",
                        strokeLinejoin: "miter",
                        strokeMiterlimit: 10,
                        fill: "currentColor",
                        fillRule: "nonzero",
                        opacity: 1,
                      }}
                      transform=" matrix(1 0 0 1 0 0) "
                      strokeLinecap="round"
                    />
                    <path
                      d="M 55.451 90 H 34.549 c -1.712 0 -3.099 -1.387 -3.099 -3.099 s 1.388 -3.099 3.099 -3.099 h 20.901 c 1.712 0 3.099 1.387 3.099 3.099 S 57.163 90 55.451 90 z"
                      style={{
                        stroke: "none",
                        strokeWidth: 2,
                        strokeDasharray: "none",
                        strokeLinecap: "butt",
                        strokeLinejoin: "miter",
                        strokeMiterlimit: 10,
                        fill: "currentColor",
                        fillRule: "nonzero",
                        opacity: 1,
                      }}
                      transform=" matrix(1 0 0 1 0 0) "
                      strokeLinecap="round"
                    />
                  </g>
                </svg>
              </Button>
            )}
            <Button
              type="button"
              onClick={handleSendClick}
              variant="ghost"
              size="sm"
              disabled={disabled || isTyping}
              title={isTyping ? 'Waiting for the current response…' : undefined}
              className={`flex-shrink-0 w-12 h-12 p-0 border-2 rounded-full touch-manipulation ${
                hasText && !isTyping
                  ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90 active:bg-primary/80'
                  : 'bg-transparent text-muted-foreground border-input'
              }`}
            >
              <ArrowUp className="h-8 w-8" strokeWidth={2} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
